import { db, functions } from '../firebase/client';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

export interface NotificationPayload {
    title: string;
    body: string;
    icon?: string;
    data?: any;
}

class NotificationService {
    // Show a local notification if permission is granted
    static async showLocalNotification(payload: NotificationPayload) {
        if (typeof window === 'undefined' || !("Notification" in window)) return;

        const Notification = (window as any).Notification;
        if (Notification.permission === "granted") {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration) {
                registration.showNotification(payload.title, {
                    body: payload.body,
                    icon: payload.icon || '/logo-red.svg',
                    badge: '/logo-red.svg',
                    tag: 'yumii-notification',
                    renotify: true,
                    vibrate: [200, 100, 200],
                    requireInteraction: true,
                    data: payload.data,
                } as any);
            } else {
                new Notification(payload.title, {
                    body: payload.body,
                    icon: payload.icon || '/logo-red.svg',
                    tag: 'yumii-notification'
                } as any);
            }
        }
    }

    /**
     * Send a push notification via Firebase Cloud Function (FCM v1)
     * This avoids exposing the Server Key on the client side.
     */
    static async sendToUser(uid: string, payload: NotificationPayload) {
        try {
            console.log(`🚀 [NOTIFICATION] Triggering Push via Cloud Function for user ${uid}...`, { title: payload.title });

            const sendPush = httpsCallable(functions, 'sendPushNotification');
            const result: any = await sendPush({
                uid,
                title: payload.title,
                body: payload.body,
                icon: payload.icon || '/logo-red.svg',
                data: payload.data
            });

            if (result.data?.success) {
                console.log(`✅ [NOTIFICATION] FCM Success for ${uid}:`, result.data.messageId || "Sent");
            } else {
                console.warn(`⚠️ [NOTIFICATION] FCM partial failure for ${uid}:`, result.data?.error || "Unknown error");
            }
            return result.data;
        } catch (error: any) {
            console.error(`❌ [NOTIFICATION] Cloud Function Error for ${uid}:`, {
                code: error.code,
                message: error.message,
                details: error.details
            });
            // We NO LONGER fallback to local notification here because this service 
            // is called by the SENDER of the notification. 
            // Showing a local notification would alert the sender instead of the recipient.
        }
    }

    // Specialized Helpers
    static notifyClient(uid: string, title: string, body: string, data?: any) {
        return this.sendToUser(uid, { title, body, data: { ...data, type: 'order_update' } });
    }

    static notifyRestaurant(uid: string, title: string, body: string, data?: any) {
        // Use 'nudge' type for relance, or standard 'new_order' if generic
        return this.sendToUser(uid, { title, body, data: { ...data, type: 'nudge' } });
    }
}

export default NotificationService;
